/**
 * @fileoverview
 * Registra geradores de código Arduino para os blocos das extensões customizadas.
 * Sem isso, workspaceToCode() falha com "Cannot read properties of undefined (reading 'call')"
 * ao encontrar um bloco customizado no workspace.
 *
 * Referência: openblock-blocks generators/arduino/
 * https://wiki.openblock.cc/zh/developer-guide/software-development
 */

const ORDER_ATOMIC = 0;

/**
 * Registra no gerador Arduino as funções de geração de código para os blocos da extensão.
 * @param {object} ScratchBlocks - Instância do ScratchBlocks (Blockly) com Arduino generator
 * @param {string} extensionId - ID da extensão (ex.: 'photoResistorSensor')
 */
export const registerCustomExtensionGenerators = (ScratchBlocks, extensionId) => {
    const Arduino = ScratchBlocks && ScratchBlocks.Arduino;
    if (!Arduino || typeof Arduino !== 'object') return;

    const order = Arduino.ORDER_ATOMIC != null ? Arduino.ORDER_ATOMIC : ORDER_ATOMIC;

    switch (extensionId) {
    case 'photoResistorSensor':
        Arduino['photoResistorSensor_readValue'] = function (block) {
            const pin = block.getFieldValue('PIN') || 'A0';
            const code = 'analogRead(' + pin + ')';
            return [code, order];
        };
        break;

    case 'ultrasonicSensor':
        Arduino['ultrasonicSensor_readDistance'] = function (block) {
            const trigger = block.getFieldValue('TRIGGER') || '9';
            const echo = block.getFieldValue('ECHO') || '10';
            // setups_ vai para void setup(); definitions_ fica no escopo global (inválido para pinMode).
            if (Arduino.setups_) {
                Arduino.setups_['ultrasonic_setup_' + trigger + '_' + echo] =
                    'pinMode(' + trigger + ', OUTPUT);\n  pinMode(' + echo + ', INPUT);';
            }
            const ultrasonicFunctionName = 'getDistanceFromUltrasonic_' + trigger + '_' + echo;
            if (Arduino.customFunctions_ && !Arduino.customFunctions_[ultrasonicFunctionName]) {
                Arduino.customFunctions_[ultrasonicFunctionName] =
                    'float ' + ultrasonicFunctionName + '() {\n' +
                    '  digitalWrite(' + trigger + ', LOW);\n' +
                    '  delayMicroseconds(2);\n' +
                    '  digitalWrite(' + trigger + ', HIGH);\n' +
                    '  delayMicroseconds(10);\n' +
                    '  digitalWrite(' + trigger + ', LOW);\n' +
                    '  long distance = pulseIn(' + echo + ', HIGH, 30000) / 58.0;\n' +
                    '  delay(10);\n' +
                    '  return distance;\n' +
                    '}';
            }
            return [ultrasonicFunctionName + '()', order];
        };
        break;

    case 'linetracingIRSensor':
        Arduino['linetracingIRSensor_readValue'] = function (block) {
            const pin = block.getFieldValue('PIN') || 'A0';
            const code = 'analogRead(' + pin + ')';
            return [code, order];
        };
        Arduino['linetracingIRSensor_isBlack'] = function (block) {
            const pin = block.getFieldValue('PIN') || 'A0';
            const code = '(analogRead(' + pin + ') < 512)';
            return [code, order];
        };
        break;

    case 'dht11Sensor': {
        const dht11Gen = (block, method) => {
            const pin = block.getFieldValue('PIN') || '2';
            if (Arduino.includes_) Arduino.includes_['include_dht11'] = '#include <DHT.h>';
            if (Arduino.definitions_) {
                Arduino.definitions_['definitions_dht11_' + pin] = 'DHT dht11_sensor_' + pin + '(' + pin + ', DHT11);';
            }
            if (Arduino.setups_) {
                Arduino.setups_['setups_dht11_' + pin] = 'dht11_sensor_' + pin + '.begin();';
            }
            const code = 'dht11_sensor_' + pin + '.' + method + '()';
            return [code, order];
        };
        Arduino['dht11Sensor_readTemperature'] = function (block) {
            return dht11Gen(block, 'readTemperature');
        };
        Arduino['dht11Sensor_readHumidity'] = function (block) {
            return dht11Gen(block, 'readHumidity');
        };
        break;
    }

    case 'liquidCrystalI2c': {
        const lcdInstanceName = (addr) => 'lcd_i2c_' + (addr || '0x27').replace('0x', '');

        Arduino['liquidCrystalI2c_init'] = function (block) {
            const addr = block.getFieldValue('ADDR') || '0x27';
            const cols = block.getFieldValue('COLS') || '16';
            const rows = block.getFieldValue('ROWS') || '2';
            const name = lcdInstanceName(addr);
            if (Arduino.includes_) {
                Arduino.includes_['include_wire'] = '#include <Wire.h>';
                Arduino.includes_['include_liquidCrystalI2c'] = '#include <LiquidCrystal_I2C.h>';
            }
            if (Arduino.definitions_) {
                Arduino.definitions_['lcd_i2c_def_' + name] =
                    'LiquidCrystal_I2C ' + name + '(' + addr + ', ' + cols + ', ' + rows + ');';
            }
            if (Arduino.setups_) {
                Arduino.setups_['lcd_i2c_setup_' + name] = name + '.init(); ' + name + '.backlight();';
            }
            return '';
        };

        Arduino['liquidCrystalI2c_printAt'] = function (block) {
            const addr = block.getFieldValue('ADDR') || '0x27';
            const row = block.getFieldValue('ROW') || '0';
            const col = block.getFieldValue('COL') || '0';
            const text = Arduino.valueToCode(block, 'TEXT', order) || '""';
            const name = lcdInstanceName(addr);
            return name + '.setCursor(' + col + ', ' + row + ');\n  ' + name + '.print(' + text + ');\n';
        };

        Arduino['liquidCrystalI2c_clear'] = function (block) {
            const addr = block.getFieldValue('ADDR') || '0x27';
            const name = lcdInstanceName(addr);
            return name + '.clear();\n';
        };
        break;
    }

    case 'tb6612fng': {
        Arduino['tb6612fng_init'] = function (block) {
            const pwma = block.getFieldValue('PWMA') || '5';
            const ain1 = block.getFieldValue('AIN1') || '6';
            const ain2 = block.getFieldValue('AIN2') || '7';
            const pwmb = block.getFieldValue('PWMB') || '9';
            const bin1 = block.getFieldValue('BIN1') || '10';
            const bin2 = block.getFieldValue('BIN2') || '11';
            if (Arduino.definitions_) {
                Arduino.definitions_['tb6612_defines'] =
                    '#define TB6612_PWMA ' + pwma + '\n  #define TB6612_AIN1 ' + ain1 +
                    '\n  #define TB6612_AIN2 ' + ain2 + '\n  #define TB6612_PWMB ' + pwmb +
                    '\n  #define TB6612_BIN1 ' + bin1 + '\n  #define TB6612_BIN2 ' + bin2;
            }
            if (Arduino.setups_) {
                Arduino.setups_['tb6612_setup'] =
                    'pinMode(TB6612_PWMA, OUTPUT); pinMode(TB6612_AIN1, OUTPUT); pinMode(TB6612_AIN2, OUTPUT); ' +
                    'pinMode(TB6612_PWMB, OUTPUT); pinMode(TB6612_BIN1, OUTPUT); pinMode(TB6612_BIN2, OUTPUT);';
            }
            return '';
        };

        Arduino['tb6612fng_motorA'] = function (block) {
            const dir = block.getFieldValue('DIR') || 'forward';
            const speed = block.getFieldValue('SPEED') || '200';
            let code = '';
            if (dir === 'forward') {
                code = 'digitalWrite(TB6612_AIN1, HIGH); digitalWrite(TB6612_AIN2, LOW); analogWrite(TB6612_PWMA, ' + speed + ');';
            } else if (dir === 'backward') {
                code = 'digitalWrite(TB6612_AIN1, LOW); digitalWrite(TB6612_AIN2, HIGH); analogWrite(TB6612_PWMA, ' + speed + ');';
            } else {
                code = 'digitalWrite(TB6612_AIN1, LOW); digitalWrite(TB6612_AIN2, LOW); analogWrite(TB6612_PWMA, 0);';
            }
            return code + '\n';
        };

        Arduino['tb6612fng_motorB'] = function (block) {
            const dir = block.getFieldValue('DIR') || 'forward';
            const speed = block.getFieldValue('SPEED') || '200';
            let code = '';
            if (dir === 'forward') {
                code = 'digitalWrite(TB6612_BIN1, HIGH); digitalWrite(TB6612_BIN2, LOW); analogWrite(TB6612_PWMB, ' + speed + ');';
            } else if (dir === 'backward') {
                code = 'digitalWrite(TB6612_BIN1, LOW); digitalWrite(TB6612_BIN2, HIGH); analogWrite(TB6612_PWMB, ' + speed + ');';
            } else {
                code = 'digitalWrite(TB6612_BIN1, LOW); digitalWrite(TB6612_BIN2, LOW); analogWrite(TB6612_PWMB, 0);';
            }
            return code + '\n';
        };
        break;
    }

    case 'l298n': {
        Arduino['l298n_init'] = function (block) {
            const ena = block.getFieldValue('ENA') || '5';
            const in1 = block.getFieldValue('IN1') || '6';
            const in2 = block.getFieldValue('IN2') || '7';
            const enb = block.getFieldValue('ENB') || '9';
            const in3 = block.getFieldValue('IN3') || '10';
            const in4 = block.getFieldValue('IN4') || '11';
            if (Arduino.definitions_) {
                Arduino.definitions_['l298n_defines'] =
                    '#define L298N_ENA ' + ena + '\n  #define L298N_IN1 ' + in1 +
                    '\n  #define L298N_IN2 ' + in2 + '\n  #define L298N_ENB ' + enb +
                    '\n  #define L298N_IN3 ' + in3 + '\n  #define L298N_IN4 ' + in4;
            }
            if (Arduino.setups_) {
                Arduino.setups_['l298n_setup'] =
                    'pinMode(L298N_ENA, OUTPUT); pinMode(L298N_IN1, OUTPUT); pinMode(L298N_IN2, OUTPUT); ' +
                    'pinMode(L298N_ENB, OUTPUT); pinMode(L298N_IN3, OUTPUT); pinMode(L298N_IN4, OUTPUT);';
            }
            return '';
        };

        Arduino['l298n_motorA'] = function (block) {
            const dir = block.getFieldValue('DIR') || 'forward';
            const speed = block.getFieldValue('SPEED') || '200';
            let code = '';
            if (dir === 'forward') {
                code = 'digitalWrite(L298N_IN1, HIGH); digitalWrite(L298N_IN2, LOW); analogWrite(L298N_ENA, ' + speed + ');';
            } else if (dir === 'backward') {
                code = 'digitalWrite(L298N_IN1, LOW); digitalWrite(L298N_IN2, HIGH); analogWrite(L298N_ENA, ' + speed + ');';
            } else {
                code = 'digitalWrite(L298N_IN1, LOW); digitalWrite(L298N_IN2, LOW); analogWrite(L298N_ENA, 0);';
            }
            return code + '\n';
        };

        Arduino['l298n_motorB'] = function (block) {
            const dir = block.getFieldValue('DIR') || 'forward';
            const speed = block.getFieldValue('SPEED') || '200';
            let code = '';
            if (dir === 'forward') {
                code = 'digitalWrite(L298N_IN3, HIGH); digitalWrite(L298N_IN4, LOW); analogWrite(L298N_ENB, ' + speed + ');';
            } else if (dir === 'backward') {
                code = 'digitalWrite(L298N_IN3, LOW); digitalWrite(L298N_IN4, HIGH); analogWrite(L298N_ENB, ' + speed + ');';
            } else {
                code = 'digitalWrite(L298N_IN3, LOW); digitalWrite(L298N_IN4, LOW); analogWrite(L298N_ENB, 0);';
            }
            return code + '\n';
        };
        break;
    }

    case 'tcrt5000Digital':
        Arduino['tcrt5000Digital_readValue'] = function (block) {
            const pin = block.getFieldValue('PIN') || '2';
            const code = '(digitalRead(' + pin + ') == HIGH)';
            return [code, order];
        };
        break;

    case 'mpu6050': {
        Arduino['mpu6050_init'] = function (block) {
            const addr = block.getFieldValue('ADDR') || '0x68';
            if (Arduino.includes_) {
                Arduino.includes_['include_mpu6050'] = '#include <Adafruit_MPU6050.h>';
                Arduino.includes_['include_sensor'] = '#include <Adafruit_Sensor.h>';
            }
            if (Arduino.definitions_) {
                Arduino.definitions_['mpu6050_obj'] = 'Adafruit_MPU6050 mpu;';
            }
            if (Arduino.setups_) {
                Arduino.setups_['mpu6050_begin'] = 'mpu.begin(' + addr + ');';
            }
            return '';
        };

        const mpu6050AddGetter = (blockType, funcName, expr) => {
            Arduino[blockType] = function (block) {
                if (Arduino.customFunctions_ && !Arduino.customFunctions_[funcName]) {
                    Arduino.customFunctions_[funcName] =
                        'float ' + funcName + '() {\n  sensors_event_t a, g, temp;\n  mpu.getEvent(&a, &g, &temp);\n  return ' + expr + ';\n}';
                }
                return [funcName + '()', order];
            };
        };
        mpu6050AddGetter('mpu6050_readAccelX', 'mpu6050_getAccelX', 'a.acceleration.x');
        mpu6050AddGetter('mpu6050_readAccelY', 'mpu6050_getAccelY', 'a.acceleration.y');
        mpu6050AddGetter('mpu6050_readAccelZ', 'mpu6050_getAccelZ', 'a.acceleration.z');
        mpu6050AddGetter('mpu6050_readGyroX', 'mpu6050_getGyroX', 'g.gyro.x');
        mpu6050AddGetter('mpu6050_readGyroY', 'mpu6050_getGyroY', 'g.gyro.y');
        mpu6050AddGetter('mpu6050_readGyroZ', 'mpu6050_getGyroZ', 'g.gyro.z');
        mpu6050AddGetter('mpu6050_readTemperature', 'mpu6050_getTemp', 'temp.temperature');
        break;
    }

    default:
        break;
    }
};

/**
 * Remove os geradores registrados para uma extensão (evitar referências órfãs ao descarregar).
 * Opcional: o gerador pode permanecer; ao gerar código sem o bloco, não é chamado.
 * @param {object} ScratchBlocks - Instância do ScratchBlocks
 * @param {string} extensionId - ID da extensão
 */
export const unregisterCustomExtensionGenerators = (ScratchBlocks, extensionId) => {
    const Arduino = ScratchBlocks && ScratchBlocks.Arduino;
    if (!Arduino) return;

    const opcodes = {
        photoResistorSensor: ['photoResistorSensor_readValue'],
        ultrasonicSensor: ['ultrasonicSensor_readDistance'],
        linetracingIRSensor: ['linetracingIRSensor_readValue', 'linetracingIRSensor_isBlack'],
        dht11Sensor: ['dht11Sensor_readTemperature', 'dht11Sensor_readHumidity'],
        liquidCrystalI2c: ['liquidCrystalI2c_init', 'liquidCrystalI2c_printAt', 'liquidCrystalI2c_clear'],
        tb6612fng: ['tb6612fng_init', 'tb6612fng_motorA', 'tb6612fng_motorB'],
        l298n: ['l298n_init', 'l298n_motorA', 'l298n_motorB'],
        tcrt5000Digital: ['tcrt5000Digital_readValue'],
        mpu6050: ['mpu6050_init', 'mpu6050_readAccelX', 'mpu6050_readAccelY', 'mpu6050_readAccelZ', 'mpu6050_readGyroX', 'mpu6050_readGyroY', 'mpu6050_readGyroZ', 'mpu6050_readTemperature']
    };
    const list = opcodes[extensionId];
    if (list) {
        list.forEach(opcode => delete Arduino[opcode]);
    }
};
